/*
 * Статусная капсула. Смысл несёт текст, цвет — только ускоритель сканирования колонки,
 * поэтому в forced-colors тон теряется без потери информации (разбор — в контракте).
 *
 * Цвета собираются в два шага: класс тона объявляет палитру роли (--status-tint/-text/-fill/…),
 * класс варианта решает, что из неё пойдёт в фон, текст и рамку. Справа от каждой
 * локальной переменной стоит токен, не значение.
 *
 * Медиазапросов по ширине нет: плотность выбирается ступенью, а не экраном.
 */

.root {
  --status-h: var(--status-badge-h-sm);
  --status-px: var(--space-3);
  --status-gap: var(--space-2);
  --status-font: var(--font-size-md);
  --status-font-mono: var(--font-size-sm);
  --status-dot-size: var(--status-dot-sm);

  /* Палитра роли по умолчанию — нейтральный тон: незнакомый статус из 1С красится серым */
  --status-tint: var(--color-neutral-tint);
  --status-text: var(--color-neutral-text);
  --status-border: var(--color-neutral-border);
  --status-fill: var(--gradient-neutral);
  --status-fill-text: var(--color-text-on-accent);
  --status-fill-edge: var(--color-neutral-edge);

  /*
   * Точка тона красится тушью тона (--status-text), а не заливкой. Посчитано: 8-пиксельная
   * точка --gradient-warning на --color-warning-tint даёт 1.27:1 по верхнему стопу и 1.71:1
   * по нижнему при пороге 3:1 для значащей графики — жёлтое на бледно-жёлтом не видно вовсе.
   * Тушь держит 5.6:1 и переворачивается вместе с темой. Точка и подпись становятся одного
   * цвета — это и правильнее: они говорят одно и то же.
   *
   * Тот же дефект нашёлся у точки наличия в проводнике каталога и вылечен там так же.
   * У варианта solid маркер остаётся тушью «на заливке» (--status-fill-text, ниже).
   */
  --status-marker: var(--status-text);
  --status-ring: var(--color-neutral-tint);

  /* Итог варианта: перекрывается классами .tint / .solid / .quiet */
  --status-bg: var(--status-tint);
  --status-fg: var(--status-text);
  --status-edge: var(--status-border);
  --status-gloss: var(--gloss-top);

  display: inline-flex;
  box-sizing: border-box;
  gap: var(--status-gap);
  align-items: center;
  max-width: 100%;
  min-height: var(--status-h);
  padding: 0 var(--status-px);
  color: var(--status-fg);
  font-size: var(--status-font);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
  line-height: var(--line-height-text);
  letter-spacing: var(--tracking-control);
  vertical-align: middle;
  background: var(--status-bg);
  border: var(--border-hairline) solid var(--status-edge);
  border-radius: var(--radius-pill);
  box-shadow: var(--status-gloss);
}

/* Шкала: своя, не --control-h-*. Кит: 22/24/26 → xs, 28/30 → sm, 32/34 → md */

.xs {
  --status-h: var(--status-badge-h-xs);
  --status-px: var(--space-25);
  --status-gap: var(--space-15);
  --status-font: var(--font-size-sm);
  --status-font-mono: var(--font-size-xs);
  --status-dot-size: var(--status-dot-xs);
}

.sm {
  --status-h: var(--status-badge-h-sm);
  --status-px: var(--space-3);
  --status-gap: var(--space-2);
  --status-font: var(--font-size-md);
  --status-font-mono: var(--font-size-sm);
  --status-dot-size: var(--status-dot-sm);
}

.md {
  --status-h: var(--status-badge-h-md);
  --status-px: var(--space-35);
  --status-gap: var(--space-2);
  --status-font: var(--font-size-xl);
  --status-font-mono: var(--font-size-lg);
  --status-dot-size: var(--status-dot-md);
}

/* Палитра тона */

.toneSuccess {
  --status-tint: var(--color-success-tint);
  --status-text: var(--color-success-text);
  --status-border: var(--color-success-border);
  --status-fill: var(--gradient-success);
  --status-fill-text: var(--color-success-text-on-solid);
  --status-fill-edge: var(--color-success-edge);
  --status-ring: var(--color-success-tint);
}

.toneWarning {
  --status-tint: var(--color-warning-tint);
  --status-text: var(--color-warning-text);
  --status-border: var(--color-warning-border);
  --status-fill: var(--gradient-warning);
  --status-fill-text: var(--color-warning-text-on-solid);
  --status-fill-edge: var(--color-warning-edge);
  --status-ring: var(--color-warning-tint);
}

/*
 * Красный текст: Q9 слил три красных кита в два токена, и светлый из них (--color-danger-text)
 * на 12-процентной подложке даёт 4.2:1 — ниже AA. Статус читают глазами построчно, поэтому
 * здесь берётся -strong (6.0:1).
 */
.toneDanger {
  --status-tint: var(--color-danger-tint);
  --status-text: var(--color-danger-text-strong);
  --status-border: var(--color-danger-border);
  --status-fill: var(--gradient-danger);
  --status-fill-text: var(--color-danger-text-on-solid);
  --status-fill-edge: var(--color-danger-edge);
  --status-ring: var(--color-danger-tint);
}

.toneAccent {
  --status-tint: var(--color-accent-tint);
  --status-text: var(--color-accent-text);
  --status-border: var(--color-accent-border);
  --status-fill: var(--gradient-accent);
  --status-fill-text: var(--color-accent-text-on-solid);
  --status-fill-edge: var(--color-accent-edge);
  --status-ring: var(--color-accent-tint);
}

.toneNeutral {
  --status-tint: var(--color-neutral-tint);
  --status-text: var(--color-neutral-text);
  --status-border: var(--color-neutral-border);
  --status-fill: var(--gradient-neutral);
  --status-fill-text: var(--color-text-on-accent);
  --status-fill-edge: var(--color-neutral-edge);
  --status-ring: var(--color-neutral-tint);
}

/* Варианты: выбирают, что из палитры тона идёт в фон, текст и рамку */

.tint {
  --status-bg: var(--status-tint);
  --status-fg: var(--status-text);
  --status-edge: var(--status-border);
  --status-gloss: var(--gloss-top);
}

/*
 * Плотная заливка. Тени кита (0 1px 2px тона) не переносим: под капсулой 28px капля не читается,
 * объём держит верхний блик. Точка на заливке — сплошная, цветом текста.
 */
.solid {
  --status-bg: var(--status-fill);
  --status-fg: var(--status-fill-text);
  --status-edge: var(--status-fill-edge);
  --status-gloss: var(--gloss-top-on-accent);
  --status-marker: var(--status-fill-text);
  --status-ring: var(--color-surface-on-accent);
}

/* Нейтральная капсула с тоновым текстом: остаток «1 284 шт», архивные строки */
.quiet {
  --status-bg: var(--color-surface);
  --status-fg: var(--status-text);
  --status-edge: var(--color-border);
  --status-gloss: var(--gloss-top);
}

/* Статус из восьми слов в колонке 240px: обрезается здесь, а не на корне — иначе кольцо пульса срежется */
.text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Идентификаторы: номер накладной, ИНН, ETD. Моноглифы шире, поэтому кегль на ступень ниже */
.mono .text {
  font-family: var(--font-mono);
  font-size: var(--status-font-mono);
  font-weight: var(--font-weight-medium);
  letter-spacing: normal;
}

/* Точка — состояние. Градиент того же тона (правило кита) */
.dot {
  flex: 0 0 auto;
  width: var(--status-dot-size);
  height: var(--status-dot-size);
  background: var(--status-marker);
  border-radius: var(--radius-circle);
}

/*
 * Живой канал: связь с 1С, живые остатки. Кадры @keyframes pulse — общие, в app/styles/base.css.
 * Они берут цвет кольца из --color-accent-tint, поэтому токен переопределён тинтом текущего тона:
 * иначе у красного «Нет связи с 1С» расходилось бы синее кольцо. Переопределение заперто
 * внутри точки — потомков у неё нет, наружу не протекает.
 * prefers-reduced-motion гасится глобальным блоком: остаётся обычная точка, смысл в тексте.
 */
.dotPulse {
  --color-accent-tint: var(--status-ring);

  animation: var(--anim-pulse);
}

.icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
}

/*
 * Капсула держится на заливке и рамке: в высокой контрастности Windows и то, и другое
 * сбрасывается. Тон при этом теряется — это допустимо, смысл несёт текст.
 */
@media (forced-colors: active) {
  .root {
    color: CanvasText;
    background: Canvas;
    border-color: CanvasText;
  }

  .dot {
    background: CanvasText;
  }
}

/* Фраза для скринридера вместо видимого текста; места в потоке не занимает */
.label {
  composes: visuallyHidden from '@/shared/styles/visuallyHidden.module.css';
}
